import { VarsHelper } from "@swivl-mono/helpers";
import {
  AppType,
  CortexSetting,
  FACILITY_INFO,
  FacilityInfoDef,
  FacilityInfoKey,
  FacilityInfoSelectOption,
  LocationScope,
  LocationVarInheritance,
  LocationVarKey,
  MergedVarsWithKeys,
  VarDef,
  VarType
} from "@swivl-mono/types";
import { Button, ButtonColor, ButtonSize, ImageUploader, ImageUploaderType, Input, Select, SelectSize, TextArea, Tooltip } from "@swivl-mono/ui";
import { useEffect, useState } from "react";
import { BiEditAlt } from "react-icons/bi";
import { GrRevert } from "react-icons/gr";

import { useMergedLocationVariables } from "../../../../../Hooks/useLocationVariables";
import { usePropertiesGetLocationIds } from "../../../../../Hooks/usePropertiesGetLocationIds";
import AppsModel from "../../../../../Models/Apps/Apps.Model";
import FMSModel from "../../../../../Models/FMS/FMS.Model";
import { WidgetSwitch } from "../../../../Widgets/WidgetSwitch";
import { CortexInheritancePill } from "../../Components/CortexInheritancePill";
import { revertLabelFor } from "../../Components/revertTarget";
import { TopicProvider, useTopicContext, useTopicHandlers, useTopicRow } from "../../Hooks/useTopicContext";

export const TOPIC_VARS: LocationVarKey[] = [
  ...FACILITY_INFO.map((f) => f.cortexVarKey),
  LocationVarKey.CortexCurrencyCode,
  LocationVarKey.CortexLengthUnit,
  LocationVarKey.CortexFacilityFMSSyncEnabled,
  LocationVarKey.CortexPushConversationSummaryToFms,
  LocationVarKey.TwilioAppId
];

const PLACEHOLDERS: Record<string, string> = {
  string: "",
  phone: "(555) 123-4567",
  email: "name@example.com",
  url: "https://...",
  hours: "Mon-Fri 9am-6pm"
};

/**
 * Stand-in for a facility with no photo yet. The uploader always renders an
 * image, so an empty field needs something to show.
 */
const FACILITY_IMAGE_PLACEHOLDER = "https://swivl-cdn.s3-us-west-1.amazonaws.com/avatar-default-org.png";

/** Edge of the square uploader thumbnail, in px, sized to sit in one card row. */
const FACILITY_IMAGE_SIZE = 96;

/** Per-field hints, for fields whose type alone gives nothing useful to show. */
const FIELD_PLACEHOLDERS: Partial<Record<FacilityInfoKey, string>> = {
  [FacilityInfoKey.State]: "CA",
  [FacilityInfoKey.PostalCode]: "94107"
};

const CURRENCY_SETTING: CortexSetting = {
  kind: "select",
  key: "currency",
  varKey: LocationVarKey.CortexCurrencyCode,
  label: "Currency",
  description:
    "Currency used for pricing everywhere this facility is shown — bot responses and widget cards (unit picker, reservations, account balances).",
  options: [
    { value: "USD", label: "US Dollar (USD)" },
    { value: "CAD", label: "Canadian Dollar (CAD)" },
    { value: "GBP", label: "British Pound (GBP)" },
    { value: "EUR", label: "Euro (EUR)" },
    { value: "AUD", label: "Australian Dollar (AUD)" },
    { value: "NZD", label: "New Zealand Dollar (NZD)" }
  ],
  default: "USD"
};

const LENGTH_UNIT_SETTING: CortexSetting = {
  kind: "select",
  key: "lengthUnit",
  varKey: LocationVarKey.CortexLengthUnit,
  label: "Length unit",
  description: "Unit of measure used when the bot quotes unit dimensions or distances.",
  options: [
    { value: "ft", label: "Feet (imperial)" },
    { value: "m", label: "Metres (metric)" }
  ],
  default: "ft"
};

/** A few legacy rows are "false" instead of "0" - check both. */
function isTruthy(value: string | undefined): boolean {
  return value === "1" || value === "true";
}

/** Plain var, not part of the FACILITY_INFO registry. Cortex key only (ENG-3388 C-9). */
const FMS_SYNC_ENABLED_SETTING: CortexSetting = {
  kind: "toggle",
  key: "fmsSyncEnabled",
  varKey: LocationVarKey.CortexFacilityFMSSyncEnabled,
  label: "FMS Sync Enabled",
  description: "Enable or disable the syncing of FMS data.",
  default: isTruthy(varDefault(LocationVarKey.CortexFacilityFMSSyncEnabled))
};

const PUSH_CONVERSATION_SUMMARY_TO_FMS_SETTING: CortexSetting = {
  kind: "toggle",
  key: "pushConversationSummaryToFms",
  varKey: LocationVarKey.CortexPushConversationSummaryToFms,
  label: "Push conversation summaries to FMS",
  description:
    "When enabled, every conversation with a tenant gets a summary written as a note directly in the FMS. Summaries with no matching tenant will only live in swivl.",
  default: isTruthy(varDefault(LocationVarKey.CortexPushConversationSummaryToFms))
};

/** Plain var, not part of the FACILITY_INFO registry. Options swapped in live below. */
const TWILIO_APP_SETTING: CortexSetting = {
  kind: "select",
  key: "twilioAppId",
  varKey: LocationVarKey.TwilioAppId,
  label: "Outbound Phone Number",
  description: "The swivl-owned phone number this facility calls and texts from.",
  options: [],
  default: varDefault(LocationVarKey.TwilioAppId)
};

interface FieldGroup {
  id: string;
  title: string;
  caption?: string;
  /** Fields drawn from the FACILITY_INFO registry. */
  keys?: FacilityInfoKey[];
  /** Fields declared inline, for vars the registry doesn't cover. */
  settings?: CortexSetting[];
  /** Inline settings spliced in immediately after a given registry key's row. */
  extraSettingsAfter?: Partial<Record<FacilityInfoKey, CortexSetting[]>>;
}

/**
 * One card per group, ordered by how often an operator touches them. System
 * comes last: those identifiers are set once at implementation and editing them
 * breaks the FMS sync, so they shouldn't sit among the daily facility details.
 */
const FIELD_GROUPS: FieldGroup[] = [
  {
    id: "facility",
    title: "Facility",
    caption: "Who this property is and where it's located.",
    keys: [
      FacilityInfoKey.Name,
      FacilityInfoKey.Address,
      FacilityInfoKey.City,
      FacilityInfoKey.State,
      FacilityInfoKey.PostalCode,
      FacilityInfoKey.ImageURL
    ]
  },
  {
    id: "contact",
    title: "Contact",
    caption: "How customers reach this facility.",
    keys: [
      FacilityInfoKey.Phone,
      FacilityInfoKey.Email,
      FacilityInfoKey.WebsiteURL,
      FacilityInfoKey.PayByPhoneNumber,
      FacilityInfoKey.AfterHoursPhoneNumber
    ],
    extraSettingsAfter: { [FacilityInfoKey.AfterHoursPhoneNumber]: [TWILIO_APP_SETTING] }
  },
  {
    id: "district-manager",
    title: "District Manager",
    caption: "The manager responsible for this facility.",
    keys: [FacilityInfoKey.DMName, FacilityInfoKey.DMPhone, FacilityInfoKey.DMEmail]
  },
  {
    id: "bot",
    title: "Bot",
    caption: "How the assistant introduces itself and opens a conversation.",
    keys: [FacilityInfoKey.BotName, FacilityInfoKey.Persona]
  },
  {
    id: "system",
    title: "System",
    caption: "Identifiers that sync this facility with your FMS. Change these only with swivl.",
    keys: [FacilityInfoKey.LocationNumber, FacilityInfoKey.FMSId, FacilityInfoKey.FMSIntegrationId, FacilityInfoKey.BrandId],
    extraSettingsAfter: {
      [FacilityInfoKey.FMSIntegrationId]: [FMS_SYNC_ENABLED_SETTING, PUSH_CONVERSATION_SUMMARY_TO_FMS_SETTING]
    }
  },
  {
    id: "localization",
    title: "Localization",
    caption: "Units of measure and currency the bot uses for this facility's region.",
    settings: [CURRENCY_SETTING, LENGTH_UNIT_SETTING]
  }
];

const GROUPED_KEYS = new Set<FacilityInfoKey>(FIELD_GROUPS.flatMap((g) => g.keys ?? []));

/** Registry fields added later still render, rather than silently disappearing. */
const UNGROUPED_FIELDS: readonly FacilityInfoDef[] = FACILITY_INFO.filter((f) => !GROUPED_KEYS.has(f.key));

/**
 * The built-in default for a var, as declared in VarDef — the value a field
 * falls back to when nothing is set at any scope. This is the same source the
 * server merges from, so the two agree.
 */
function varDefault(varKey: LocationVarKey): string {
  return VarDef[VarsHelper.varKeyForType(VarType.Location, varKey)]?.defaultValue ?? "";
}

/** Placeholder hint for a field, prefixed so it reads as an example not a value. */
function placeholderFor(field: FacilityInfoDef): string {
  const hint = FIELD_PLACEHOLDERS[field.key] ?? PLACEHOLDERS[field.fieldType] ?? "";
  return hint ? `e.g. ${hint}` : "";
}

function buildSetting(field: FacilityInfoDef, fmsOptions: FacilityInfoSelectOption[]): CortexSetting {
  if (field.fieldType === "image") {
    return {
      kind: "image",
      key: field.key,
      varKey: field.cortexVarKey,
      label: field.label,
      description: field.description,
      placeholderImageURL: FACILITY_IMAGE_PLACEHOLDER,
      default: varDefault(field.cortexVarKey),
      helpUrl: field.helpUrl
    };
  }
  if (field.fieldType === "select") {
    return {
      kind: "select",
      key: field.key,
      varKey: field.cortexVarKey,
      label: field.label,
      description: field.description,
      options: field.key === FacilityInfoKey.FMSIntegrationId ? fmsOptions : (field.selectOptions ?? []),
      default: varDefault(field.cortexVarKey),
      helpUrl: field.helpUrl
    };
  }
  return {
    kind: "text",
    key: field.key,
    varKey: field.cortexVarKey,
    label: field.label,
    description: field.description,
    placeholder: placeholderFor(field),
    default: varDefault(field.cortexVarKey),
    helpUrl: field.helpUrl
  };
}

function settingsForGroup(group: FieldGroup, fmsOptions: FacilityInfoSelectOption[], twilioAppOptions: FacilityInfoSelectOption[]): CortexSetting[] {
  if (group.settings) return group.settings;
  return (group.keys ?? []).flatMap((key) => {
    const field = FACILITY_INFO.find((f) => f.key === key);
    const built = field ? [buildSetting(field, fmsOptions)] : [];
    const extra = (group.extraSettingsAfter?.[key] ?? []).map((setting) =>
      setting.key === TWILIO_APP_SETTING.key ? { ...setting, options: twilioAppOptions } : setting
    );
    return [...built, ...extra];
  });
}

/** What the page calls the entity being edited. Only a location is a physical facility. */
export const SCOPE_NOUN: Record<LocationScope, string> = {
  [LocationScope.ORG]: "Organization",
  [LocationScope.GROUP]: "Group",
  [LocationScope.LOCATION]: "Facility"
};

/** The Name field's registry label says "Facility Name"; name the selected scope instead. */
function withScopedNameLabel(settings: CortexSetting[], scope: LocationScope): CortexSetting[] {
  return settings.map((setting) => (setting.key === FacilityInfoKey.Name ? { ...setting, label: `${SCOPE_NOUN[scope]} Name` } : setting));
}

/** The value as plain text. Selects resolve to their option label. */
function readOnlyText(setting: CortexSetting, value: string): string {
  if (setting.kind === "toggle") return isTruthy(value) ? "On" : "Off";
  if (setting.kind === "select") return setting.options.find((o) => o.value === value)?.label ?? value;
  // The value is an upload URL. Naming it says nothing, so describe it instead.
  if (setting.kind === "image") return value ? "the inherited image" : "";
  return value;
}

export function FacilityInfoTopic(): React.ReactElement {
  const { FMSs } = FMSModel.useState();
  const { apps } = AppsModel.useState();
  const ctx = useTopicContext();
  const page = usePropertiesGetLocationIds();
  // What a field falls back to once its override here is deleted: whatever the
  // next scope up resolves to. Resolving one scope out gives exactly that. At org
  // scope there's nothing above, so the fallback is the VarDef default instead.
  const isOrgScope = page.type === LocationScope.ORG;
  const parentVars = useMergedLocationVariables(TOPIC_VARS, page.locationId != null ? page.groupId : undefined, undefined);
  // Which card is open. The worksheet owns the page-level editing flag (it drives
  // the save bar); this narrows it to a single card.
  const [openGroupId, setOpenGroupId] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!FMSs) FMSModel.load(false);
  }, [FMSs]);

  useEffect(() => {
    if (!apps) AppsModel.load(false);
  }, [apps]);

  // Save / Cancel in the bar clears the worksheet's flag — close the card to match.
  useEffect(() => {
    if (!ctx.isEditing && openGroupId) setOpenGroupId(undefined);
  }, [ctx.isEditing, openGroupId]);

  // The org's configured FMS integrations, not a static list — options for FacilityInfoKey.FMSIntegrationId.
  const fmsOptions = (FMSs ?? []).map((fms) => ({ value: String(fms.id), label: `${fms.name} (${fms.type})` }));

  // The org's Twilio number instances — options for the Outbound Twilio App setting.
  const twilioAppOptions = (apps ?? [])
    .filter((app) => app.type === AppType.TWILIO && app.active)
    .map((app) => ({ value: String(app.id), label: app.identifier ? `${app.name} (${app.identifier})` : app.name }));

  const handleEdit = (groupId: string): void => {
    setOpenGroupId(groupId);
    ctx.setEditing(true);
  };

  const groups: FieldGroup[] =
    UNGROUPED_FIELDS.length > 0 ? [...FIELD_GROUPS, { id: "other", title: "Other", keys: UNGROUPED_FIELDS.map((f) => f.key) }] : FIELD_GROUPS;

  return (
    <div className="flex flex-col gap-4">
      {groups.map((group) => (
        <FacilityCard
          key={group.id}
          group={group}
          settings={withScopedNameLabel(settingsForGroup(group, fmsOptions, twilioAppOptions), page.type)}
          isEditing={ctx.isEditing && openGroupId === group.id}
          isOrgScope={isOrgScope}
          parentVars={parentVars}
          onEdit={() => handleEdit(group.id)}
        />
      ))}
    </div>
  );
}

// ─── Card ──────────────────────────────────────────────────────────────────

interface FacilityCardProps {
  group: FieldGroup;
  settings: CortexSetting[];
  isEditing: boolean;
  /** No scope above org, so a revert there lands on the VarDef default. */
  isOrgScope: boolean;
  /** Values resolved one scope up — what a revert falls back to. */
  parentVars: MergedVarsWithKeys<LocationVarKey> | undefined;
  onEdit: () => void;
}

function FacilityCard({ group, settings, isEditing, isOrgScope, parentVars, onEdit }: FacilityCardProps): React.ReactElement {
  const ctx = useTopicContext();

  return (
    <section className="overflow-hidden rounded-lg border border-grey-200 bg-white">
      <div className="flex items-center justify-between gap-4 px-4 py-3">
        <div className="min-w-0">
          <h3 className="heading-base-alt text-grey-900">{group.title}</h3>
          {group.caption && <p className="body-sm mt-0.5 text-grey-600">{group.caption}</p>}
        </div>
        {isEditing ? (
          <div className="flex shrink-0 items-center gap-2">
            <Button color={ButtonColor.WHITE} size={ButtonSize.SMALL} disabled={ctx.saving} onClick={ctx.cancelEdits}>
              Cancel
            </Button>
            <Button color={ButtonColor.ORANGE} size={ButtonSize.SMALL} disabled={ctx.saving} onClickAsync={ctx.saveTopic}>
              {ctx.saving ? "Saving…" : "Save"}
            </Button>
          </div>
        ) : (
          <button
            type="button"
            onClick={onEdit}
            className="body-sm-alt flex shrink-0 cursor-pointer items-center gap-2 text-grey-900 hover:text-orange-500"
          >
            Edit
            <BiEditAlt className="size-4" />
          </button>
        )}
      </div>

      {/* Only this card's rows see edit mode; the rest of the topic stays read-only. */}
      <TopicProvider {...ctx} isEditing={isEditing}>
        <div className="border-t border-grey-200">
          {settings.map((setting) => (
            <FacilityFieldRow
              key={setting.key}
              setting={setting}
              isOrgScope={isOrgScope}
              parentInheritance={parentVars?.[setting.varKey]?.inheritance}
              revertsTo={readOnlyText(setting, isOrgScope ? String(setting.default ?? "") : (parentVars?.[setting.varKey]?.value ?? "")) || "(empty)"}
            />
          ))}
        </div>
      </TopicProvider>
    </section>
  );
}

// ─── Row ───────────────────────────────────────────────────────────────────

interface FacilityFieldRowProps {
  setting: CortexSetting;
  /** Org is the top scope — a revert there lands on the VarDef default. */
  isOrgScope: boolean;
  /** How the next scope up resolves — names the revert's destination. */
  parentInheritance: LocationVarInheritance | undefined;
  /** Display text for the value a revert would restore. */
  revertsTo: string;
}

function FacilityFieldRow({ setting, isOrgScope, parentInheritance, revertsTo }: FacilityFieldRowProps): React.ReactElement {
  const { merged, draft } = useTopicRow(setting.varKey);
  const handlers = useTopicHandlers(setting.varKey);
  const { isEditing } = useTopicContext();

  const draftValue = draft?.value;
  const overrideStaged = draftValue !== undefined;
  const isOverride = merged.inheritance === LocationVarInheritance.NONE;
  // A staged revert (ENG-3057): the field already shows the destination value, so the
  // pill shows the destination tier and the action undoes the revert instead of repeating it.
  const revertStaged = draft?.revert === true;
  const defaultAsVar = setting.kind === "toggle" ? (setting.default ? "1" : "0") : String(setting.default ?? "");
  const displayValue = draftValue ?? merged.value ?? defaultAsVar;

  const setValue = (value: string): void => handlers.onSettingValue(value);

  const revertLabel = revertLabelFor({ isOrgScope, isOverride, inheritance: merged.inheritance, parentInheritance });

  const handleRevert = (): void => {
    if (!window.confirm(`${revertLabel}? This will discard the current customization for this field.`)) return;
    // A saved override is deleted server-side, which also clears this row's
    // draft. With nothing saved yet, dropping the draft is the whole job.
    if (isOverride) void handlers.onRevert(merged);
    else handlers.onDiscard();
  };

  // mx rather than px so the row's own border stops short of the card edge,
  // lining the divider up with the label and the tag.
  return (
    <div className="mx-4 grid grid-cols-[200px_minmax(0,1fr)_auto] items-center gap-x-4 border-b border-grey-200 py-3 last:border-b-0">
      <span className="body-sm-alt text-grey-900">{setting.label}</span>

      {/* The control gets a column of its own so the description below can share
          it and match its width exactly, rather than running under the tag. */}
      <div className="min-w-0">
        {setting.kind === "toggle" ? (
          // Always shown, dimmed outside edit mode — same as the Amenities rows.
          <div className="flex items-center">
            <WidgetSwitch on={isTruthy(displayValue)} disabled={!isEditing} onSet={(on) => setValue(on ? "1" : "0")} ariaLabel={setting.label} />
          </div>
        ) : /* Checked ahead of the read-only branch: an image shows as a thumbnail
               in both modes, and only gains its edit controls while editing. */
        setting.kind === "image" ? (
          <ImageUploader
            type={ImageUploaderType.AVATAR}
            isEditable={isEditing}
            size={FACILITY_IMAGE_SIZE}
            imageURL={displayValue || undefined}
            defaultImageURL={setting.placeholderImageURL}
            imageUploaded={setValue}
            onImageDeleted={() => setValue("")}
          />
        ) : !isEditing ? (
          <span className="body-sm break-words text-grey-900">{readOnlyText(setting, displayValue) || "—"}</span>
        ) : setting.kind === "select" ? (
          <Select
            size={SelectSize.MEDIUM}
            hideClear
            selectedValues={[displayValue]}
            options={setting.options.map((o) => ({ label: o.label, value: o.value }))}
            onChange={(values) => {
              if (values[0] !== undefined) setValue(values[0]);
            }}
          />
        ) : setting.kind === "textarea" ? (
          <TextArea
            // A textarea is inline-block by default, so its container picks up a
            // text line box and leaves phantom space underneath.
            className="block"
            value={displayValue}
            rows={setting.rows}
            placeholder={setting.placeholder}
            onChange={(e) => setValue(e.target.value)}
          />
        ) : (
          <Input
            type={setting.kind === "number" ? "number" : "text"}
            value={displayValue}
            placeholder={setting.kind === "text" ? setting.placeholder : undefined}
            onChange={(e) => setValue(e.target.value)}
          />
        )}
      </div>

      <div className="flex shrink-0 items-center gap-2">
        <CortexInheritancePill
          inheritance={
            revertStaged
              ? (draft?.revertInheritance ?? LocationVarInheritance.DEFAULT)
              : isOverride || overrideStaged
                ? LocationVarInheritance.NONE
                : merged.inheritance
          }
        />

        {isEditing && revertStaged && (
          <button
            type="button"
            onClick={handlers.onDiscard}
            className="body-sm-alt flex shrink-0 cursor-pointer items-center gap-2 text-grey-900 hover:text-orange-500"
          >
            Undo revert
            <GrRevert className="size-4" />
          </button>
        )}

        {isEditing && !revertStaged && (isOverride || overrideStaged) && (
          <Tooltip content={`Reverts to: ${revertsTo}`}>
            <button
              type="button"
              onClick={handleRevert}
              className="body-sm-alt flex shrink-0 cursor-pointer items-center gap-2 text-grey-900 hover:text-orange-500"
            >
              {revertLabel}
              <GrRevert className="size-4" />
            </button>
          </Tooltip>
        )}
      </div>

      {/* Second grid row, control column — matches the control's width and keeps
          the label centred on it. */}
      {isEditing && setting.description && <p className="body-sm col-start-2 mt-1 text-grey-600">{setting.description}</p>}
    </div>
  );
}
